/**
 * RBAC Role values — aligned with Prisma `Role` enum (schema.prisma).
 * Defined here (not imported from db-core) so shared types stay mock-friendly in Jest,
 * where many tests override `jest.mock('db-core', ...)`.
 */
export const Role = {
  USER: 'USER',
  MODERATOR: 'MODERATOR',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
} as const;

export type Role = (typeof Role)[keyof typeof Role];

const USER_PERMISSIONS = [
  'events:browse',
  'events:save',
  'profile:read',
  'profile:update',
] as const;

const MODERATOR_PERMISSIONS = [
  ...USER_PERMISSIONS,
  'events:flag',
  'events:hide',
  'content:review_reported',
] as const;

const ADMIN_PERMISSIONS = [
  ...MODERATOR_PERMISSIONS,
  'users:manage',
  'audit_logs:view',
  'categories:manage',
  // API-1360: gates admin sponsored-listing routes (M1) — creating/editing
  // sponsorships and viewing reconciliation data. Independent of the
  // FEATURE_SPONSORED_LISTINGS kill-switch (services/backend-api/src/config/env.ts),
  // which gates the sponsored-slot injector's output, not route access.
  'sponsorships:manage',
] as const;

const SUPER_ADMIN_PERMISSIONS = [
  ...ADMIN_PERMISSIONS,
  'roles:manage',
  'system:config',
  'system:destructive_actions',
] as const;

/** Union of all valid permission strings, derived from the role arrays. */
export type Permission =
  | (typeof USER_PERMISSIONS)[number]
  | (typeof MODERATOR_PERMISSIONS)[number]
  | (typeof ADMIN_PERMISSIONS)[number]
  | (typeof SUPER_ADMIN_PERMISSIONS)[number];

/** Each role's allowed actions. Higher roles inherit all lower-role permissions. */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  [Role.USER]: USER_PERMISSIONS,
  [Role.MODERATOR]: MODERATOR_PERMISSIONS,
  [Role.ADMIN]: ADMIN_PERMISSIONS,
  [Role.SUPER_ADMIN]: SUPER_ADMIN_PERMISSIONS,
} as const;

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Lowest → highest privilege. Higher index satisfies lower-index requirements. */
export const ROLE_HIERARCHY = [Role.USER, Role.MODERATOR, Role.ADMIN, Role.SUPER_ADMIN] as const;

const ROLE_LEVEL = new Map<Role, number>(ROLE_HIERARCHY.map((role, index) => [role, index]));

export function roleSatisfiesMinimum(userRole: Role, minimumRole: Role): boolean {
  const userLevel = ROLE_LEVEL.get(userRole);
  const minLevel = ROLE_LEVEL.get(minimumRole);
  if (userLevel === undefined || minLevel === undefined) {
    return false;
  }
  return userLevel >= minLevel;
}

const ALL_PERMISSION_STRINGS = new Set<string>([
  ...USER_PERMISSIONS,
  ...MODERATOR_PERMISSIONS,
  ...ADMIN_PERMISSIONS,
  ...SUPER_ADMIN_PERMISSIONS,
]);

export function isPermissionString(value: string): value is Permission {
  return ALL_PERMISSION_STRINGS.has(value);
}
